/** Side midpoints + corners of a cell — attractors for the next step. */
const EXIT_PORTALS: readonly {
  dx: number;
  dy: number;
  u: number;
  v: number;
  corner: boolean;
}[] = [
  { dx: 1, dy: 0, u: 1, v: 0.5, corner: false },
  { dx: -1, dy: 0, u: 0, v: 0.5, corner: false },
  { dx: 0, dy: 1, u: 0.5, v: 1, corner: false },
  { dx: 0, dy: -1, u: 0.5, v: 0, corner: false },
  { dx: 1, dy: 1, u: 1, v: 1, corner: true },
  { dx: 1, dy: -1, u: 1, v: 0, corner: true },
  { dx: -1, dy: 1, u: 0, v: 1, corner: true },
  { dx: -1, dy: -1, u: 0, v: 0, corner: true },
];

type ExitPortal = (typeof EXIT_PORTALS)[number];

/** Nearest exit portal of `from` toward the pointer (or null in soft center). */
export function bestExitPortal(
  from: { x: number; y: number },
  worldX: number,
  worldY: number,
): { portal: ExitPortal; dist: number; outside: boolean } | null {
  const u = worldX - from.x;
  const v = worldY - from.y;
  const ox = u - 0.5;
  const oy = v - 0.5;

  // Still in the soft center → no direction yet.
  if (Math.abs(ox) < 0.22 && Math.abs(oy) < 0.22) return null;

  const outside = u < 0 || u > 1 || v < 0 || v > 1;
  // When far away, project onto the tip cell so a straight drag east
  // lands on the east side midpoint — not a biased far corner.
  const pu = Math.min(1, Math.max(0, u));
  const pv = Math.min(1, Math.max(0, v));

  let best: ExitPortal | null = null;
  let bestScore = Infinity;
  let bestDist = Infinity;

  for (const p of EXIT_PORTALS) {
    // Only portals roughly toward the pointer from the cell center.
    if (p.dx * ox + p.dy * oy <= 0) continue;
    const du = pu - p.u;
    const dv = pv - p.v;
    const dist = Math.hypot(du, dv);
    // Sides need to clearly win — corners catch a wider zone.
    const score = p.corner ? dist * 0.72 : dist;
    if (score < bestScore) {
      bestScore = score;
      bestDist = dist;
      best = p;
    }
  }

  if (!best) return null;
  return { portal: best, dist: bestDist, outside };
}

/**
 * Pick the neighbor by snapping the pointer to the nearest exit portal
 * of `from`. Near a corner → diagonal; near a side midpoint → ortho.
 * Corners are biased so diagonals are easy to hit.
 */
export function pickExitNeighbor(
  from: { x: number; y: number },
  worldX: number,
  worldY: number,
): { x: number; y: number } {
  const hit = bestExitPortal(from, worldX, worldY);
  if (!hit) return { ...from };

  // Commit when outside the tip cell, or when close enough to a portal
  // (so a corner drag can step before the cursor fully leaves the cell).
  if (!hit.outside && hit.dist > 0.48) return { ...from };

  return { x: from.x + hit.portal.dx, y: from.y + hit.portal.dy };
}

/**
 * Continuous rubber-band tip inside the tip cell: grows from the center
 * toward the winning exit portal as the pointer approaches it.
 * Makes cable drawing feel like a stretch, not cell-by-cell pops.
 */
export function stretchTipToward(
  from: { x: number; y: number },
  worldX: number,
  worldY: number,
): { x: number; y: number; progress: number } {
  const cx = from.x + 0.5;
  const cy = from.y + 0.5;
  const ox = worldX - cx;
  const oy = worldY - cy;
  const raw = Math.hypot(ox, oy);
  if (raw < 0.04) return { x: cx, y: cy, progress: 0 };

  const hit = bestExitPortal(from, worldX, worldY);
  if (!hit) {
    // Soft center: short free stretch toward the pointer.
    const t = Math.min(0.28, raw);
    return { x: cx + (ox / raw) * t, y: cy + (oy / raw) * t, progress: t / 0.48 };
  }

  const portalX = from.x + hit.portal.u;
  const portalY = from.y + hit.portal.v;
  // 0 at center, ~1 near commit threshold / outside.
  const progress = hit.outside
    ? 1
    : Math.min(1, Math.max(0.12, raw / 0.48));
  return {
    x: cx + (portalX - cx) * progress,
    y: cy + (portalY - cy) * progress,
    progress,
  };
}

export class Camera {
  offsetX = 0;
  offsetY = 0;
  zoom = 1;
  cellSize = 48;
  private dragging = false;
  private lastX = 0;
  private lastY = 0;

  /**
   * Fit the evolving playfield: zoom from virtual size (lerp toward next
   * expansion), while centering the current grid inside that frame.
   */
  fitEvolvingGrid(
    currentWidth: number,
    currentHeight: number,
    virtualWidth: number,
    virtualHeight: number,
    viewWidth: number,
    viewHeight: number,
    padding = 36,
  ): void {
    const vW = Math.max(currentWidth, virtualWidth);
    const vH = Math.max(currentHeight, virtualHeight);
    const worldW = vW * this.cellSize;
    const worldH = vH * this.cellSize;
    const scale = Math.min(
      (viewWidth - padding * 2) / worldW,
      (viewHeight - padding * 2) / worldH,
    );
    this.zoom = Math.max(0.2, Math.min(2.5, scale));
    const padX = ((vW - currentWidth) / 2) * this.cellSize * this.zoom;
    const padY = ((vH - currentHeight) / 2) * this.cellSize * this.zoom;
    this.offsetX = (viewWidth - worldW * this.zoom) / 2 + padX;
    this.offsetY = (viewHeight - worldH * this.zoom) / 2 + padY;
  }

  fitGrid(
    gridWidth: number,
    gridHeight: number,
    viewWidth: number,
    viewHeight: number,
    padding = 36,
  ): void {
    this.fitEvolvingGrid(
      gridWidth,
      gridHeight,
      gridWidth,
      gridHeight,
      viewWidth,
      viewHeight,
      padding,
    );
  }

  worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return {
      x: wx * this.cellSize * this.zoom + this.offsetX,
      y: wy * this.cellSize * this.zoom + this.offsetY,
    };
  }

  screenToWorld(sx: number, sy: number): { x: number; y: number } {
    const wx = (sx - this.offsetX) / (this.cellSize * this.zoom);
    const wy = (sy - this.offsetY) / (this.cellSize * this.zoom);
    return { x: wx, y: wy };
  }

  screenToCell(sx: number, sy: number): { x: number; y: number } {
    const w = this.screenToWorld(sx, sy);
    return { x: Math.floor(w.x), y: Math.floor(w.y) };
  }

  /**
   * Next cell from `from` toward the pointer.
   * Exit zones: near a corner → diagonal, near a side midpoint → ortho.
   */
  pickNeighborToward(
    sx: number,
    sy: number,
    from: { x: number; y: number },
  ): { x: number; y: number } {
    const w = this.screenToWorld(sx, sy);
    return pickExitNeighbor(from, w.x, w.y);
  }

  pan(dx: number, dy: number): void {
    this.offsetX += dx;
    this.offsetY += dy;
  }

  zoomAt(factor: number, sx: number, sy: number): void {
    const before = this.screenToWorld(sx, sy);
    this.zoom = Math.max(0.25, Math.min(3, this.zoom * factor));
    const after = this.screenToWorld(sx, sy);
    this.offsetX += (after.x - before.x) * this.cellSize * this.zoom;
    this.offsetY += (after.y - before.y) * this.cellSize * this.zoom;
  }

  beginPan(sx: number, sy: number): void {
    this.dragging = true;
    this.lastX = sx;
    this.lastY = sy;
  }

  movePan(sx: number, sy: number): boolean {
    if (!this.dragging) return false;
    this.pan(sx - this.lastX, sy - this.lastY);
    this.lastX = sx;
    this.lastY = sy;
    return true;
  }

  endPan(): void {
    this.dragging = false;
  }

  isPanning(): boolean {
    return this.dragging;
  }
}
